import Phaser from 'phaser';
import { BaseScene } from '@core/scenes/BaseScene';
import { Palette, STAGE_BG_TEXTURE } from '@core/config/constants';
import { OptionButton } from '@ui/components/OptionButton';
import { TriviaConfig, TRIVIA_QUESTIONS, type TriviaQuestion } from '@modules/trivia/config';

const MAX_OPTION_WIDTH = 720;
const OPTION_SPACING = 92;
const FIRST_OPTION_Y_RATIO = 0.42;

/**
 * Minijuego de trivia: muestra preguntas de opcion multiple, da feedback de
 * correcto/incorrecto y suma puntos. Tercer ejemplo del patron modular; reutiliza
 * el componente OptionButton para los botones de respuesta.
 */
export class TriviaScene extends BaseScene {
  private questions: TriviaQuestion[] = [];
  private currentIndex = 0;
  private score = 0;
  private optionButtons: OptionButton[] = [];
  private questionObjects: Phaser.GameObjects.GameObject[] = [];
  private scoreText!: Phaser.GameObjects.Text;
  private progressText!: Phaser.GameObjects.Text;

  public constructor() {
    super(TriviaConfig.key);
  }

  public create(): void {
    this.initResponsive();
    this.resetState();
    this.addBackground(STAGE_BG_TEXTURE);

    this.scoreText = this.add.text(40, 30, '', {
      fontFamily: 'Trebuchet MS, sans-serif',
      fontSize: '34px',
      color: Palette.textLight,
    });

    this.progressText = this.add
      .text(this.scale.width - 40, 30, '', {
        fontFamily: 'Trebuchet MS, sans-serif',
        fontSize: '34px',
        color: Palette.textLight,
      })
      .setOrigin(1, 0);

    this.renderQuestion();
  }

  private resetState(): void {
    this.questions = Phaser.Utils.Array.Shuffle([...TRIVIA_QUESTIONS]).slice(
      0,
      TriviaConfig.questionsPerRound,
    );
    this.currentIndex = 0;
    this.score = 0;
    this.optionButtons = [];
    this.questionObjects = [];
  }

  private renderQuestion(): void {
    this.clearQuestion();
    this.refreshHud();

    const question = this.questions[this.currentIndex];

    const questionText = this.add
      .text(this.centerX, this.scale.height * 0.24, question.question, {
        fontFamily: 'Trebuchet MS, sans-serif',
        fontSize: '36px',
        color: Palette.textLight,
        align: 'center',
        wordWrap: { width: this.scale.width * 0.8 },
      })
      .setOrigin(0.5);
    this.questionObjects.push(questionText);

    const firstY = this.scale.height * FIRST_OPTION_Y_RATIO;
    const optionWidth = Math.min(MAX_OPTION_WIDTH, this.scale.width * 0.9);
    question.options.forEach((option, index) => {
      const button = new OptionButton(this, {
        x: this.centerX,
        y: firstY + index * OPTION_SPACING,
        width: optionWidth,
        label: option,
        onSelect: () => this.handleAnswer(index),
      });
      this.optionButtons.push(button);
    });
  }

  private handleAnswer(selectedIndex: number): void {
    const question = this.questions[this.currentIndex];
    const isCorrect = selectedIndex === question.correctIndex;

    this.optionButtons.forEach((button, index) => {
      button.disable();
      if (index === question.correctIndex) {
        button.markCorrect();
      } else if (index === selectedIndex) {
        button.markWrong();
      }
    });

    if (isCorrect) {
      this.score += TriviaConfig.pointsPerCorrect;
      this.audio.playCorrect();
      this.refreshHud();
    } else {
      this.audio.playWrong();
    }

    this.time.delayedCall(TriviaConfig.feedbackDelayMs, () => this.advance());
  }

  private advance(): void {
    this.currentIndex += 1;
    if (this.currentIndex >= this.questions.length) {
      this.endRound();
      return;
    }
    this.renderQuestion();
  }

  private clearQuestion(): void {
    this.optionButtons.forEach((button) => button.destroy());
    this.questionObjects.forEach((object) => object.destroy());
    this.optionButtons = [];
    this.questionObjects = [];
  }

  private refreshHud(): void {
    this.scoreText.setText(`Puntos: ${this.score}`);
    this.progressText.setText(`Pregunta ${this.currentIndex + 1}/${this.questions.length}`);
  }

  private endRound(): void {
    this.clearQuestion();

    const isRecord = this.profile.submitScore(TriviaConfig.key, this.score);
    const coins = Math.floor(this.score / 100) * TriviaConfig.coinsPer100Points;
    this.profile.addCoins(coins);

    this.showRoundResults({
      title: isRecord ? '¡Nuevo record!' : '¡Bien hecho!',
      score: this.score,
      coins,
    });
  }
}
